import { useState, useEffect, useCallback } from 'react';
import { Book, Search, ChevronDown, ChevronRight, Clock, AlertTriangle, Activity } from 'lucide-react';
import api, { type WikiPage, type WikiSearchResult, type WikiHealth, type WikiIngestLogEntry } from '../api';

interface Props {
  projectId: string;
}

const ENTITY_TYPES = [
  'pipeline_phase', 'model', 'feature', 'decision',
  'pattern', 'integration', 'concept', 'developer_profile',
] as const;

const ENTITY_LABELS: Record<string, string> = {
  pipeline_phase: 'Pipeline Phases',
  model: 'Models',
  feature: 'Features',
  decision: 'Decisions',
  pattern: 'Patterns',
  integration: 'Integrations',
  concept: 'Concepts',
  developer_profile: 'Developer Profiles',
};

const STATUS_COLORS: Record<string, string> = {
  active: '#22c55e',
  deprecated: '#ef4444',
  planned: '#3b82f6',
  experimental: '#eab308',
};

export default function WikiVault({ projectId }: Props) {
  const [pages, setPages] = useState<WikiPage[]>([]);
  const [selectedPage, setSelectedPage] = useState<WikiPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<WikiSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [health, setHealth] = useState<WikiHealth | null>(null);
  const [logs, setLogs] = useState<WikiIngestLogEntry[]>([]);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set(ENTITY_TYPES));
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const loadPages = useCallback(() => {
    setLoading(true);
    const opts: { status?: string } = {};
    if (statusFilter !== 'all') opts.status = statusFilter;
    api.wiki.pages(projectId, opts)
      .then(setPages)
      .catch(() => setPages([]))
      .finally(() => setLoading(false));
  }, [projectId, statusFilter]);

  const loadHealth = useCallback(() => {
    api.wiki.health(projectId).then(setHealth).catch(() => {});
  }, [projectId]);

  const loadLogs = useCallback(() => {
    api.wiki.log(projectId, 10).then(setLogs).catch(() => {});
  }, [projectId]);

  useEffect(() => { loadPages(); }, [loadPages]);
  useEffect(() => { loadHealth(); loadLogs(); }, [loadHealth, loadLogs]);

  const openPage = useCallback((slug: string) => {
    setPageLoading(true);
    api.wiki.page(projectId, slug)
      .then((p: WikiPage) => setSelectedPage(p))
      .catch(() => setSelectedPage(null))
      .finally(() => setPageLoading(false));
  }, [projectId]);

  const doSearch = useCallback(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    setSearching(true);
    api.wiki.search(projectId, searchQuery.trim())
      .then(setSearchResults)
      .catch(() => setSearchResults([]))
      .finally(() => setSearching(false));
  }, [projectId, searchQuery]);

  const toggleType = (type: string) => {
    setExpandedTypes((prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  // Group pages by entity_type
  const grouped = new Map<string, WikiPage[]>();
  for (const type of ENTITY_TYPES) grouped.set(type, []);
  for (const page of pages) {
    const arr = grouped.get(page.entity_type);
    if (arr) arr.push(page);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Book size={18} style={{ color: 'var(--blue)' }} />
        <h3 style={{ margin: 0, fontSize: 16 }}>Wiki Vault</h3>
        {health && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
            {health.total_pages} pages
          </span>
        )}
      </div>

      {/* Search bar */}
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={14} style={{ position: 'absolute', left: 8, top: 9, color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Semantic search wiki..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') doSearch(); }}
            style={{
              width: '100%', padding: '6px 8px 6px 28px',
              border: '1px solid var(--border)', borderRadius: 6,
              fontSize: 13, background: 'var(--bg-surface)',
            }}
          />
        </div>
        <button className="btn btn-sm" onClick={doSearch} disabled={searching}>
          {searching ? 'Searching...' : 'Search'}
        </button>
        <select
          value={statusFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(e.target.value)}
          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12 }}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="deprecated">Deprecated</option>
          <option value="planned">Planned</option>
          <option value="experimental">Experimental</option>
        </select>
      </div>

      {/* Search results */}
      {searchResults.length > 0 && (
        <div className="card" style={{ padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text-muted)' }}>
            Search Results ({searchResults.length})
          </div>
          {searchResults.map((r: WikiSearchResult) => (
            <div
              key={r.id}
              onClick={() => openPage(r.slug)}
              style={{
                padding: '8px 10px', cursor: 'pointer', borderRadius: 4,
                display: 'flex', alignItems: 'center', gap: 8,
                borderBottom: '1px solid var(--border)',
              }}
              onMouseOver={(e: React.MouseEvent<HTMLDivElement>) => (e.currentTarget.style.background = 'var(--bg-hover)')}
              onMouseOut={(e: React.MouseEvent<HTMLDivElement>) => (e.currentTarget.style.background = '')}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{r.entity_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {r.entity_type} {r.summary ? `\u2014 ${r.summary.slice(0, 80)}...` : ''}
                </div>
              </div>
              {r.similarity != null && (
                <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                  {(r.similarity * 100).toFixed(0)}%
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, minHeight: 400 }}>
        {/* Left sidebar: entity browser */}
        <div style={{ width: 260, flexShrink: 0, overflowY: 'auto', maxHeight: 600 }}>
          {loading ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>Loading...</div>
          ) : pages.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>
              No wiki pages yet. Pages are generated automatically from Manus reviews and PR merges.
            </div>
          ) : (
            ENTITY_TYPES.map((type: typeof ENTITY_TYPES[number]) => {
              const items = grouped.get(type) ?? [];
              if (items.length === 0) return null;
              const expanded = expandedTypes.has(type);
              return (
                <div key={type} style={{ marginBottom: 4 }}>
                  <div
                    onClick={() => toggleType(type)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4, padding: '6px 8px',
                      cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      color: 'var(--text-muted)', userSelect: 'none',
                    }}
                  >
                    {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    {ENTITY_LABELS[type] ?? type}
                    <span style={{
                      marginLeft: 'auto', fontSize: 10, background: 'var(--bg-surface)',
                      padding: '0 5px', borderRadius: 8, color: 'var(--text-muted)',
                    }}>
                      {items.length}
                    </span>
                  </div>
                  {expanded && items.map((page: WikiPage) => (
                    <div
                      key={page.id}
                      onClick={() => openPage(page.slug)}
                      style={{
                        padding: '5px 8px 5px 24px', cursor: 'pointer', fontSize: 12,
                        borderRadius: 4,
                        background: selectedPage?.slug === page.slug ? 'var(--bg-hover)' : '',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}
                      onMouseOver={(e: React.MouseEvent<HTMLDivElement>) => {
                        if (selectedPage?.slug !== page.slug) e.currentTarget.style.background = 'var(--bg-hover)';
                      }}
                      onMouseOut={(e: React.MouseEvent<HTMLDivElement>) => {
                        if (selectedPage?.slug !== page.slug) e.currentTarget.style.background = '';
                      }}
                    >
                      <span style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: STATUS_COLORS[page.status] ?? '#888',
                        flexShrink: 0,
                      }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {page.entity_name}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>

        {/* Main panel: page viewer */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {pageLoading ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>Loading page...</div>
          ) : selectedPage ? (
            <div className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <h4 style={{ margin: 0, fontSize: 15 }}>{selectedPage.entity_name}</h4>
                <span style={{
                  fontSize: 10, padding: '1px 6px', borderRadius: 4,
                  background: 'rgba(59,130,246,0.08)', color: 'var(--blue)',
                }}>
                  {selectedPage.entity_type}
                </span>
                <span style={{
                  fontSize: 10, padding: '1px 6px', borderRadius: 4,
                  background: `${STATUS_COLORS[selectedPage.status] ?? '#888'}20`,
                  color: STATUS_COLORS[selectedPage.status] ?? '#888',
                }}>
                  {selectedPage.status}
                </span>
                <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                  v{selectedPage.version}
                  {selectedPage.last_ingest_at && (
                    <> &middot; Updated {new Date(selectedPage.last_ingest_at).toLocaleDateString()}</>
                  )}
                </span>
              </div>

              {/* Linked entities */}
              {selectedPage.linked_entity_slugs && (selectedPage.linked_entity_slugs as string[]).length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
                  {(selectedPage.linked_entity_slugs as string[]).map((slug: string) => (
                    <span
                      key={slug}
                      onClick={() => openPage(slug)}
                      style={{
                        fontSize: 10, padding: '2px 8px', borderRadius: 12, cursor: 'pointer',
                        background: 'var(--bg-surface)', border: '1px solid var(--border)',
                        color: 'var(--blue)',
                      }}
                    >
                      {slug}
                    </span>
                  ))}
                </div>
              )}

              {/* Markdown content */}
              <div style={{
                fontSize: 13, lineHeight: 1.6,
                whiteSpace: 'pre-wrap', fontFamily: 'inherit',
              }}>
                {selectedPage.content_md}
              </div>
            </div>
          ) : (
            <div style={{
              color: 'var(--text-muted)', fontSize: 13, padding: 40,
              textAlign: 'center',
            }}>
              Select a wiki page from the sidebar to view its content.
            </div>
          )}
        </div>
      </div>

      {/* Bottom bar: health + ingest log */}
      <div style={{ display: 'flex', gap: 16 }}>
        {/* Health */}
        {health && (
          <div className="card" style={{ padding: 12, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Activity size={14} style={{ color: 'var(--blue)' }} />
              <span style={{ fontSize: 12, fontWeight: 600 }}>Health</span>
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Pages: </span>
                <strong>{health.total_pages}</strong>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Orphaned: </span>
                <strong style={{ color: health.orphaned_entities > 0 ? 'var(--orange)' : undefined }}>
                  {health.orphaned_entities}
                </strong>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Stale: </span>
                <strong style={{ color: health.stale_entities > 0 ? 'var(--orange)' : undefined }}>
                  {health.stale_entities}
                </strong>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Conflicts: </span>
                <strong style={{ color: health.total_conflicts > 0 ? 'var(--red)' : undefined }}>
                  {health.total_conflicts}
                </strong>
              </div>
            </div>
          </div>
        )}

        {/* Ingest log */}
        <div className="card" style={{ padding: 12, flex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Clock size={14} style={{ color: 'var(--blue)' }} />
            <span style={{ fontSize: 12, fontWeight: 600 }}>Recent Ingests</span>
          </div>
          {logs.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No ingest runs yet.</div>
          ) : (
            <div style={{ fontSize: 11 }}>
              {logs.slice(0, 5).map((log: WikiIngestLogEntry) => (
                <div key={log.id} style={{
                  display: 'flex', gap: 12, padding: '3px 0',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <span style={{ color: 'var(--text-muted)', minWidth: 80 }}>
                    {new Date(log.created_at).toLocaleDateString()}
                  </span>
                  <span>{log.triggered_by}</span>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {log.raw_inputs_processed} inputs &rarr; +{log.entities_created} / ~{log.entities_updated}
                  </span>
                  {log.conflicts_detected > 0 && (
                    <span style={{ color: 'var(--orange)', display: 'flex', alignItems: 'center', gap: 2 }}>
                      <AlertTriangle size={10} /> {log.conflicts_detected}
                    </span>
                  )}
                  {log.duration_ms != null && (
                    <span style={{ color: 'var(--text-muted)', marginLeft: 'auto' }}>
                      {(log.duration_ms / 1000).toFixed(1)}s
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
